import { reactive, watch, computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useCompanyProfileStore } from '@/stores/companyProfileStore'
import { useIndustryStore } from '@/stores/industryStore'
import { useCompanyIndustryProfileStore } from '@/stores/companyIndustryProfileStore'
import { hasIndustryAttributeValue, industryAttributeErrors } from '@/validation/industryAttributes'
import type { CompanyProfile } from '@/types/company'
import {
  companyProfileSchema,
  mapZodErrors,
  prepareCompanyProfilePayload,
  type CompanyProfileFormValues,
} from '@/validation/companyProfileSchema'

/**
 * Composable: useCompanyProfileForm
 *
 * Purpose:
 * - Create an isolated, editable clone of the company profile for form editing
 * - Prevent accidental mutations to store data
 * - Provide reset/save logic for consistent UX
 */
export function useCompanyProfileForm() {
  const store = useCompanyProfileStore()
  const { profile, isLoading } = storeToRefs(store)
  const industryStore = useIndustryStore()
  const { industries } = storeToRefs(industryStore)
  const industryProfileStore = useCompanyIndustryProfileStore()

  const toSafeString = (value: unknown): string => (typeof value === 'string' ? value : '').trim()

  const splitLegacyName = (value: unknown) => {
    const fullName = toSafeString(value)
    if (!fullName) {
      return { first_name: '', last_name: '' }
    }
    const parts = fullName.split(/\s+/)
    const first_name = parts.shift() ?? ''
    const last_name = parts.join(' ')
    return { first_name, last_name }
  }

  const normalizeKeyPeople = (value: unknown): CompanyProfile['key_people'] => {
    if (!Array.isArray(value)) return []

    return value.map((entry) => {
      if (entry && typeof entry === 'object') {
        const firstName = toSafeString(
          (entry as any).first_name ?? (entry as any).firstName ?? (entry as any).first
        )
        const lastName = toSafeString(
          (entry as any).last_name ?? (entry as any).lastName ?? (entry as any).last
        )
        const role = toSafeString(
          (entry as any).role ?? (entry as any).title ?? (entry as any).position
        )
        const roleStartDate = toSafeString(
          (entry as any).role_start_date ?? (entry as any).roleStartDate ?? (entry as any).start_date ?? ''
        )
        if (firstName || lastName) {
          return { first_name: firstName, last_name: lastName, role, role_start_date: roleStartDate }
        }

        const legacy = splitLegacyName((entry as any).name)
        return { first_name: legacy.first_name, last_name: legacy.last_name, role, role_start_date: roleStartDate }
      }

      return { first_name: '', last_name: '', role: '', role_start_date: '' }
    })
  }

  const normalizeKeyProjects = (value: unknown): CompanyProfile['key_projects'] => {
    if (!Array.isArray(value)) return []

    return value.map((entry) => {
      if (entry && typeof entry === 'object') {
        const activityFeed = Array.isArray((entry as any).activity_feed ?? (entry as any).activityFeed)
          ? ((entry as any).activity_feed ?? (entry as any).activityFeed)
              .map((feedEntry: any) => ({
                date: toSafeString(feedEntry?.date ?? feedEntry?.released_at ?? feedEntry?.releasedAt ?? ''),
                text: toSafeString(feedEntry?.text ?? feedEntry?.update ?? feedEntry?.description ?? ''),
              }))
              .filter((feedEntry: any) => feedEntry.date || feedEntry.text)
          : []

        return {
          name: toSafeString((entry as any).name ?? ''),
          stage: toSafeString((entry as any).stage ?? ''),
          description: toSafeString((entry as any).description ?? ''),
          location_label: toSafeString(
            (entry as any).location_label ?? (entry as any).locationLabel ?? (entry as any).location ?? ''
          ),
          location_lat:
            (entry as any).location_lat ?? (entry as any).locationLat ?? (entry as any).lat ?? null,
          location_lng:
            (entry as any).location_lng ?? (entry as any).locationLng ?? (entry as any).lng ?? (entry as any).lon ?? null,
          commodities: Array.isArray((entry as any).commodities) ? (entry as any).commodities : [],
          activity_feed: activityFeed,
        }
      }

      return {
        name: '',
        stage: '',
        description: '',
        location_label: '',
        location_lat: null,
        location_lng: null,
        commodities: [],
        activity_feed: [],
      }
    })
  }

  const normalizeJobLinks = (value: unknown): CompanyProfile['job_links'] => {
    if (!Array.isArray(value)) return []

    return value
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null

        const type = toSafeString((entry as any).type).toLowerCase()
        if (!['linkedin', 'seek', 'other'].includes(type)) return null

        const url = toSafeString((entry as any).url ?? '')
        if (!url) return null

        return {
          type: type as 'linkedin' | 'seek' | 'other',
          title: toSafeString((entry as any).title ?? ''),
          description: toSafeString((entry as any).description ?? ''),
          url,
        }
      })
      .filter((entry): entry is NonNullable<CompanyProfile['job_links']>[number] => Boolean(entry))
  }

  const normalizeSocialLinks = (value: unknown): CompanyProfile['social_links'] => {
    if (!Array.isArray(value)) return []

    return value
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null

        const type = toSafeString((entry as any).type).toLowerCase()
        if (!['linkedin', 'facebook', 'instagram', 'x', 'youtube'].includes(type)) return null

        const url = toSafeString((entry as any).url ?? '')
        if (!url) return null

        return {
          type: type as 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube',
          url,
        }
      })
      .filter((entry): entry is NonNullable<CompanyProfile['social_links']>[number] => Boolean(entry))
  }

  const normalizeRecentKeyHires = (value: unknown): CompanyProfile['recent_key_hires'] => {
    if (!Array.isArray(value)) return []

    return value.map((entry) => {
      if (entry && typeof entry === 'object') {
        return {
          name: toSafeString((entry as any).name ?? ''),
          role: toSafeString((entry as any).role ?? (entry as any).title ?? ''),
          date: toSafeString((entry as any).date ?? ''),
          background: toSafeString((entry as any).background ?? '') || 'Appointment',
          summary: toSafeString((entry as any).summary ?? ''),
          url: toSafeString((entry as any).url ?? ''),
        }
      }

      return {
        name: '',
        role: '',
        date: '',
        background: 'Appointment',
        summary: '',
        url: '',
      }
    })
  }

  const normalizeRecentCapitalRaisings = (
    value: unknown
  ): CompanyProfile['recent_capital_raisings'] => {
    if (!Array.isArray(value)) return []

    return value.map((entry) => {
      if (entry && typeof entry === 'object') {
        return {
          date: toSafeString((entry as any).date ?? ''),
          amount: toSafeString((entry as any).amount ?? ''),
          label: toSafeString((entry as any).label ?? (entry as any).type ?? ''),
          summary: toSafeString((entry as any).summary ?? (entry as any).notes ?? ''),
          url: toSafeString((entry as any).url ?? ''),
        }
      }

      return {
        date: '',
        amount: '',
        label: '',
        summary: '',
        url: '',
      }
    })
  }

  const normalizeBenefits = (value: unknown): CompanyProfile['benefits'] => {
    if (!Array.isArray(value)) return []

    return value.map((entry) => {
      if (entry && typeof entry === 'object') {
        return {
          heading: toSafeString((entry as any).heading ?? (entry as any).title ?? ''),
          description: toSafeString((entry as any).description ?? (entry as any).summary ?? ''),
          source: toSafeString((entry as any).source ?? ''),
          added_at: toSafeString((entry as any).added_at ?? (entry as any).date ?? ''),
        }
      }

      return {
        heading: '',
        description: '',
        source: '',
        added_at: '',
      }
    })
  }

  // -- Step 1: Create editable form clone
  const form = reactive<CompanyProfile>({} as CompanyProfile)
  const hasInitialized = ref(false)
  const lastProfileId = ref<number | null>(null)
  const validationErrors = ref<Record<string, string>>({})
  const isPublishAttempted = ref(false)

  const normalizeProfileForForm = (data: CompanyProfile): CompanyProfile => {
    const clone = structuredClone(data)

    clone.key_people = normalizeKeyPeople(clone.key_people)
    clone.key_projects = normalizeKeyProjects(clone.key_projects)
    clone.recent_key_hires = normalizeRecentKeyHires(clone.recent_key_hires)
    clone.job_links = normalizeJobLinks(clone.job_links)
    clone.social_links = normalizeSocialLinks(clone.social_links)
    clone.recent_capital_raisings = normalizeRecentCapitalRaisings(clone.recent_capital_raisings)
    clone.benefits = normalizeBenefits(clone.benefits)
    clone.is_private_company = Boolean(clone.is_private_company)
    clone.industry_attributes =
      clone.industry_attributes && !Array.isArray(clone.industry_attributes)
        ? clone.industry_attributes
        : {}

    if (clone.industry == null) {
      clone.industry = ''
    }

    if (clone.employee_size == null) {
      clone.employee_size = ''
    }

    if (clone.headcount_growth_12_months == null) {
      clone.headcount_growth_12_months = ''
    }

    if (clone.average_employee_tenure == null) {
      clone.average_employee_tenure = ''
    }

    if (clone.contact_email == null) {
      clone.contact_email = ''
    }

    if (clone.cash_runway == null) {
      clone.cash_runway = ''
    }

    if (typeof clone.cash_in_bank_updated_at === 'string' && clone.cash_in_bank_updated_at.trim()) {
      clone.cash_in_bank_updated_at = clone.cash_in_bank_updated_at.slice(0, 10)
    }

    return clone
  }

  const normalizedProfile = computed(() => {
    if (!profile.value) return null
    return normalizeProfileForForm(profile.value)
  })

  const isDirty = computed(() => {
    if (!normalizedProfile.value) return false
    return JSON.stringify(form) !== JSON.stringify(normalizedProfile.value)
  })

  // -- Step 2: Function to reset form to latest profile
  const resetForm = () => {
    if (normalizedProfile.value) {
      Object.assign(form, normalizedProfile.value)
      validationErrors.value = {}
      isPublishAttempted.value = false
      lastProfileId.value =
        typeof normalizedProfile.value.id === 'number' ? normalizedProfile.value.id : null
    }
  }

  // Initialize form when profile is first loaded
  watch(
    profile,
    () => {
      if (!normalizedProfile.value) return

      const currentId =
        typeof normalizedProfile.value.id === 'number' ? normalizedProfile.value.id : null
      if (currentId !== null && currentId !== lastProfileId.value) {
        resetForm()
        hasInitialized.value = true
        return
      }

      if (!hasInitialized.value) {
        resetForm()
        hasInitialized.value = true
        return
      }

      if (!isDirty.value) {
        resetForm()
      }
    },
    { immediate: true }
  )

  // -- Step 3: Detect if form differs from original profile
  // (computed moved above)

  const transformForValidation = (): CompanyProfile => {
    const clone = structuredClone(form)

    if (clone.industry === '') clone.industry = null
    if (clone.employee_size === '') clone.employee_size = null
    if (clone.headcount_growth_12_months === '') clone.headcount_growth_12_months = null
    if (clone.average_employee_tenure === '') clone.average_employee_tenure = null

    return clone
  }

  const resolvedIndustryName = computed(() => {
    const industryValue = form.industry
    if (industryValue == null || industryValue === '') return ''

    const available = industries.value ?? []
    const asNumber = typeof industryValue === 'number' ? industryValue : Number(industryValue)

    if (!Number.isNaN(asNumber)) {
      const matchById = available.find((industry) => industry.id === asNumber)
      if (matchById?.name) return matchById.name
    }

    if (typeof industryValue === 'string') {
      const trimmed = industryValue.trim()
      if (!trimmed) return ''

      const lower = trimmed.toLowerCase()
      const matchByName = available.find((industry) => industry.name?.toLowerCase() === lower)
      if (matchByName?.name) return matchByName.name

      return trimmed
    }

    return ''
  })

  const isMiningIndustry = computed(() =>
    resolvedIndustryName.value.trim().toLowerCase().includes('mining')
  )

  const industryProfile = computed(() => industryProfileStore.resolve(resolvedIndustryName.value))

  const validateForPublish = () => {
    isPublishAttempted.value = true
    const candidate = transformForValidation()

    const originalIndustry = candidate.industry
    const industryForValidation =
      resolvedIndustryName.value || (originalIndustry == null ? '' : String(originalIndustry))
    const candidateForValidation = {
      ...candidate,
      industry: industryForValidation,
    }

    const result = companyProfileSchema.safeParse(candidateForValidation)
    const attributeErrors = Object.fromEntries(
      Object.entries(industryAttributeErrors(industryProfile.value, form.industry_attributes)).map(
        ([key, message]) => [`industry_attributes.${key}`, message]
      )
    )

    if (!result.success || Object.keys(attributeErrors).length) {
      validationErrors.value = {
        ...(result.success ? {} : mapZodErrors(result.error)),
        ...attributeErrors,
      }
      return { success: false as const }
    }

    result.data.industry =
      typeof originalIndustry === 'string'
        ? originalIndustry.trim()
        : originalIndustry == null
          ? industryForValidation
          : String(originalIndustry)

    validationErrors.value = {}
    return { success: true as const, data: result.data }
  }

  const preparePublishPayload = (): Partial<CompanyProfile> => {
    const result = validateForPublish()
    if (!result.success) {
      throw new Error('Validation failed')
    }

    return prepareCompanyProfilePayload(result.data as CompanyProfileFormValues)
  }

  const clearValidationState = () => {
    isPublishAttempted.value = false
    validationErrors.value = {}
  }

  const getFirstErrorForFields = (fields: readonly string[]): string | null => {
    if (!isPublishAttempted.value) return null
    for (const field of fields) {
      const direct = validationErrors.value[field]
      if (direct) return direct
    }
    for (const [key, message] of Object.entries(validationErrors.value)) {
      if (fields.some((field) => key.startsWith(`${field}.`))) {
        return message
      }
    }
    return null
  }

  const hasText = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0
  const hasNumber = (value: unknown): boolean => {
    if (typeof value === 'string') return value.trim() !== ''
    if (typeof value === 'number') return value > 0
    return false
  }
  const hasListEntries = (value: unknown): boolean =>
    Array.isArray(value) && value.filter((item) => typeof item === 'string' && item.trim() !== '').length > 0

  const hasCompletePerson = (person: any) =>
    hasText(person?.first_name) && hasText(person?.last_name) && hasText(person?.role)

  const checklistSections = computed(() => {
    const people = Array.isArray(form.key_people) ? form.key_people : []
    const projects = Array.isArray(form.key_projects) ? form.key_projects : []

    const mining = isMiningIndustry.value
    const sections = [
      {
        id: 'details',
        label: 'Company Details',
        items: [
          {
            id: 'name',
            label: 'Company name',
            description: 'Use your registered or trading business name.',
            complete: hasText(form.name),
            fields: ['name'],
            required: true,
          },
          {
            id: 'summary',
            label: 'Company summary',
            description: 'Describe your mission, operations, and focus in a short paragraph.',
            complete: hasText(form.summary),
            fields: ['summary'],
            required: true,
          },
          {
            id: 'industry',
            label: 'Select your industry',
            description: 'Help talent understand the sector you operate in.',
            complete: form.industry !== '',
            fields: ['industry'],
            required: true,
          },
          {
            id: 'logo',
            label: 'Upload logo',
            description: 'A logo builds trust with contractors.',
            complete: hasText(form.logo),
            fields: ['logo'],
            required: false,
          },
          {
            id: 'employee_size',
            label: 'Employee size',
            description: 'Share your headcount range to set team expectations.',
            complete: hasText(form.employee_size),
            fields: ['employee_size'],
            required: true,
          },
          {
            id: 'office_address',
            label: 'Head Office',
            description: 'Tell contractors where your primary office is located.',
            complete: hasText(form.office_address),
            fields: ['office_address'],
            required: true,
          },
          {
            id: 'website',
            label: 'Website',
            description: 'Provide a public website for more company context.',
            complete: hasText(form.website),
            fields: ['website'],
            required: true,
          },
        ],
      },
      {
        id: 'profile',
        label: 'Company Profile',
        items: [
          {
            id: 'people',
            label: 'Add key people',
            description: 'Introduce at least one leader or team member.',
            complete: people.some((person) => hasCompletePerson(person)),
            fields: ['key_people'],
            required: true,
          },
          ...(mining
            ? [
                {
                  id: 'market',
                  label: 'Add market data',
                  description: 'Provide a ticker or mark the company as private.',
                  complete: hasText(form.ticker) || form.is_private_company === true,
                  fields: ['ticker', 'is_private_company'],
                  required: true,
                },
                {
                  id: 'cash',
                  label: 'Cash in bank',
                  description: 'Share your available cash position in AUD.',
                  complete: hasText(form.cash_in_bank),
                  fields: ['cash_in_bank', 'cash_in_bank_updated_at', 'cash_runway'],
                  required: false,
                },
              ]
            : []),
          ...(industryProfile.value?.fields ?? []).map((field) => ({
            id: `industry_${field.key}`,
            label: field.label,
            description: field.required_message ?? `Select your ${field.label.toLowerCase()}.`,
            complete: hasIndustryAttributeValue(form.industry_attributes, field.key),
            fields: [`industry_attributes.${field.key}`],
            required: Boolean(field.required),
          })),
          ...(industryProfile.value?.sections.key_projects ?? true
            ? [
                {
                  id: 'projects',
                  label: 'Key projects',
                  description: 'Highlight at least one project with a stage.',
                  complete: projects.some((project) => hasText(project.name) && hasText(project.stage)),
                  fields: ['key_projects'],
                  required: false,
                },
              ]
            : []),
        ],
      },
      {
        id: 'operations',
        label: 'Operations Overview',
        items: [
          ...(mining
            ? [
                {
                  id: 'field_roster',
                  label: 'Field roster',
                  description: 'Explain your standard onsite roster or rotations.',
                  complete: hasText(form.field_roster),
                  fields: ['field_roster'],
                  required: false,
                },
              ]
            : []),
          {
            id: 'typical_hours',
            label: 'Typical hours',
            description: 'Set expectations for standard shift hours.',
            complete: hasText(form.typical_hours),
            fields: ['typical_hours'],
            required: false,
          },
          ...(mining
            ? [
                {
                  id: 'facilities',
                  label: 'Facilities',
                  description: 'Summarise accommodation and site facilities.',
                  complete: hasText(form.facilities),
                  fields: ['facilities'],
                  required: false,
                },
              ]
            : []),
          {
            id: 'software',
            label: 'Software used',
            description: 'List the core systems your team relies on (optional).',
            complete: hasListEntries(form.software_used),
            fields: ['software_used'],
            required: false,
          },
        ],
      },
    ]

    return sections.map((section) => {
      const items = section.items.map((item) => {
        const message = getFirstErrorForFields(item.fields)
        const resolvedComplete = message
          ? false
          : item.required
            ? item.complete
            : true

        return {
          ...item,
          message,
          complete: resolvedComplete,
        }
      })

      return {
        ...section,
        items,
        complete: items.every((item) => item.complete),
      }
    })
  })

  const checklistProgress = computed(() => {
    const sections = checklistSections.value
    const totals = sections.reduce(
      (acc, section) => {
        acc.total += section.items.length
        acc.completed += section.items.filter((item) => item.complete).length
        return acc
      },
      { total: 0, completed: 0 }
    )

    if (!totals.total) return 0
    return Math.round((totals.completed / totals.total) * 100)
  })

  const isChecklistReady = computed(
    () => hasInitialized.value && !!normalizedProfile.value && industryProfileStore.isLoaded
  )

  // -- Step 4: Placeholder for save (to be implemented next)
  const saveForm = async () => {
    console.log('Saving form...', form)
    // TODO: validate + call store.updateProfile(form)
  }

  return {
    form,
    isLoading,
    isDirty,
    resetForm,
    validationErrors,
    isPublishAttempted,
    validateForPublish,
    preparePublishPayload,
    clearValidationState,
    getFirstErrorForFields,
    checklistSections,
    checklistProgress,
    isChecklistReady,
    saveForm,
    isMiningIndustry,
    industryProfile,
  }
}
